import { now, replaceDate, reviveDate, serializeDate } from '@ewn/time';
import axios from 'axios';
import utils from 'axios/unsafe/utils.js';
import { stringify } from 'qs';

const instance = axios.create({
  baseURL: '/',
  // Arbitrary timeout since the default is infinite.
  // Should probably be lower but doesn't hurt to be safe.
  timeout: 1000 * 60 * 2,
  // We're using async/await so it's easier to use if
  // statements for control flow instead of try/catch
  validateStatus: null
});

// Let the API know we're calling from JavaScript
instance.defaults.headers.common['X-Requested-With'] = 'XMLHttpRequest';

// Source: https://github.com/axios/axios/blob/master/lib/defaults.js#L10
function setContentTypeIfUnset (headers, value) {
  if (!utils.isUndefined(headers) && utils.isUndefined(headers['Content-Type'])) {
    headers['Content-Type'] = value;
  }
}

// Adapted from axios.defaults.transformRequest:
// https://github.com/axios/axios/blob/main/lib/defaults/index.js#L55
instance.defaults.transformRequest = [(data, headers) => {
  if (utils.isFormData(data) ||
    utils.isArrayBuffer(data) ||
    utils.isBuffer(data) ||
    utils.isStream(data) ||
    utils.isFile(data) ||
    utils.isBlob(data) ||
    utils.isArrayBufferView(data) ||
    utils.isURLSearchParams(data) ||
    !utils.isObject(data)) return data;
  setContentTypeIfUnset(headers, 'application/json;charset=utf-8');
  return JSON.stringify(data, replaceDate);
}, ...axios.defaults.transformRequest];

// Adapted from axios.defaults.transformResponse:
// https://github.com/axios/axios/blob/main/lib/defaults/index.js#L118
instance.defaults.transformResponse = data => {
  if (typeof data !== 'string') return data;
  try {
    data = JSON.parse(data, reviveDate);
  } catch { /* Ignore */ }
  return data;
};

instance.interceptors.request.use(config => {
  config.paramsSerializer = params =>
    stringify(params, {
      arrayFormat: 'comma',
      serializeDate
    });

  // Send local time so server can determine user time offset and validate clock skew
  config.headers['X-Local-Time'] = serializeDate(now());

  // Note: The X-XSRF-TOKEN header is handled implicitly by axios

  return config;
});

instance.interceptors.response.use(null, error => {
  if (error.request) {
    // Network problems are expected so we want to return a null
    // response instead of throwing an error. Then, the caller can
    // use async/await without wrapping everything in try/catch
    return Promise.resolve(null);
  } else {
    // This should only occur due to issues with the config (rare)
    // so we definitely want to throw an error in this case
    return Promise.reject(error);
  }
});

export default instance;
